import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CloudOff, Loader2, LogOut, RotateCcw } from 'lucide-react';
import { ApiError, onUnauthorized } from '@/lib/api';
import { Button } from '@shining-technologies/ui';
import { authKeys, removeNonIdentityQueries, useLogout, useMe } from './queries';
import type { AdminUser } from './schema';
import { clearLegacyImpersonationStore, clearSessionState, loginPathFor } from './session';

interface AuthContextValue {
  user: AdminUser | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const { data, isLoading } = me;
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const logout = useLogout();
  const user = data ?? null;

  // The old persisted impersonation store is gone; drop its stale value once.
  useEffect(() => {
    clearLegacyImpersonationStore();
  }, []);

  // Global 401 handler: the session is gone.
  //
  // FIX(admin-401-cache-loop): clear the cached identity BEFORE redirecting.
  // Before: this only navigated to /login, leaving the user cached under
  // `authKeys.me`; LoginPage saw that truthy user and bounced straight back to
  // /dashboard, whose widgets 401'd again → a /dashboard⇄/login request storm
  // (admin/README.md "Troubleshooting"). After: cancel the in-flight burst,
  // set `me` to null (logged out), forget step-up/impersonation state, then
  // redirect with a sanitised same-origin return-to. Safe to repeat: every 401
  // clears and navigates (with `replace`, so history doesn't grow) until the
  // app is on /login, where it stops. Nothing is remembered between calls, so
  // a 401 arriving later — after the admin navigated away from /login without
  // signing in — still redirects.
  //
  // FIX(admin-401-clears-cache): also drop every other cached query. Before:
  // only `me` was reset, so the previous admin's cached lists could render for
  // whoever signed in next on this tab. After: identity AND data are cleared.
  useEffect(() => {
    return onUnauthorized(() => {
      void qc.cancelQueries();
      qc.setQueryData(authKeys.me, null);
      removeNonIdentityQueries(qc);
      clearSessionState();
      const { pathname, search, hash } = window.location;
      if (pathname === '/login' || pathname.startsWith('/login/')) return;
      navigate(loginPathFor(pathname + search + hash), { replace: true });
    });
  }, [navigate, qc]);

  // FIX(admin-401-cache-loop): /me failing with a 5xx or a network error is NOT
  // "logged out". Before: any /me error left `user` null, so ProtectedRoute
  // redirected a perfectly valid session to /login. After: with no cached
  // identity to fall back on, show a retry screen (the login page itself stays
  // usable — signing in re-seeds the cache).
  //
  // FIX(admin-me-403-disabled): only a transport failure (status 0), a server
  // error (>= 500) or an unparseable payload (not an ApiError) is a bootstrap
  // failure. Before: any error — including a 403 for a disabled account —
  // landed here with no way out. After: 401/403 resolve to null in `useMe`, any
  // other 4xx falls through to "signed out" (→ /login), and the screen below
  // offers "Sign out" as an escape hatch.
  const meError = me.error;
  const isBootstrapError =
    !(meError instanceof ApiError) || meError.status === 0 || meError.status >= 500;
  const bootstrapFailed = me.isError && data === undefined && isBootstrapError;
  const onLogin = location.pathname === '/login' || location.pathname.startsWith('/login/');
  if (bootstrapFailed && !onLogin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
        <div role="alert" className="flex max-w-sm flex-col items-center gap-3 text-center">
          <CloudOff className="h-10 w-10 text-muted-foreground" aria-hidden />
          <h1 className="text-lg font-semibold">Can’t load your session</h1>
          <p className="text-sm text-muted-foreground">
            {me.error instanceof Error && me.error.message
              ? me.error.message
              : 'The server did not respond. Check your connection and try again.'}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => void me.refetch()} disabled={me.isFetching}>
              {me.isFetching ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <RotateCcw aria-hidden />}
              Try again
            </Button>
            {/* FIX(admin-me-403-disabled): the escape hatch. Logout clears the
                local session whether or not the server answers (it may be the
                very thing that is down), which resolves `me` to null. */}
            <Button
              variant="ghost"
              disabled={logout.isPending}
              onClick={() =>
                logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })
              }
            >
              <LogOut aria-hidden />
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
